/**
 * Challenge Ticket — shareable /ticket/:code deep links (server authority).
 */
import { auth } from '../auth/firebaseAuth';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

export const TICKET_PREVIEW_TIMEOUT_MS = 6000;

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to continue.');
  return user.getIdToken();
}

// WI-6: best-effort token for the public preview. Lets a signed-in recipient's
// first view be attributed without ever requiring auth — guests preview fine.
async function getOptionalIdToken() {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

export async function previewChallengeTicket(code) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const url = `${base}/preview_challenge_ticket?code=${encodeURIComponent(String(code || '').toUpperCase())}`;
  const headers = {};
  const token = await getOptionalIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await fetchWithTimeout(url, { method: 'GET', cache: 'no-store', headers }, TICKET_PREVIEW_TIMEOUT_MS);
  } catch (err) {
    if (err?.status === 408) {
      const timeoutErr = new Error('Could not load ticket');
      timeoutErr.status = 408;
      throw timeoutErr;
    }
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const previewErr = new Error(data.error || `Preview failed (${res.status})`);
    previewErr.status = res.status;
    throw previewErr;
  }
  return data.preview;
}

export async function createChallengeTicket(payload) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/create_challenge_ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Could not create ticket (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function acceptChallengeViaTicket(code) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/accept_challenge_via_ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ code: String(code || '').toUpperCase() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not accept ticket (${res.status})`);
  return data;
}

export async function cancelChallengeTicket(code) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/cancel_challenge_ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ code: String(code || '').toUpperCase() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not cancel ticket (${res.status})`);
  return data;
}

export async function passChallengeTicket(code) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/pass_challenge_ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ code: String(code || '').toUpperCase() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Could not pass on ticket (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function listMyChallengeTickets() {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/list_my_challenge_tickets`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not load tickets (${res.status})`);
  return data;
}
